import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { getIcon } from 'site-modules/shared/utils/upper-funnel/get-svg-icon';
import { TYPE_SINGULAR_LOWERCASE_EXCEPTIONS } from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { ReadMore } from 'site-modules/upper-funnel/components/type/read-more/read-more';

import { HERO_BG_PATHS_LAUREL } from './constants';

import './hero-content.scss';

const BG_FLAG_WITH_LAUREL = getStaticImageUrl('/type-page/bgFlagWithLaurel.svg');

const DESKTOP_BG_STYLE = {
  height: '310px',
  top: '-68px',
};

const MOBILE_BG_STYLE = {
  height: '450px',
  top: '-68px',
};

export function HeroContentLaurel({ breadcrumbsData = null, title = null, type = null, subtypeTitle = null }) {
  const typeDisplayCopy = type.replace(/suv/g, 'SUV');
  const typeCopySingularLowercase = TYPE_SINGULAR_LOWERCASE_EXCEPTIONS[type] || type;
  const typeNameToUse = subtypeTitle || typeCopySingularLowercase;
  const isChildPage = !!subtypeTitle;
  const descriptionCopy = (
    <p className="size-16 mb-0">{`This page categorizes ${typeNameToUse}s by size and ranks them by their Edmunds Ratings — a detailed 10-point scoring system that reflects our testing team's comprehensive evaluation of each vehicle. This single score is backed by an in-depth analysis across seven key categories, including driving performance, comfort, cargo space, value, fuel efficiency and more. For a deeper dive into how each vehicle performs in these areas, you can explore individual vehicle pages for detailed category breakdowns.`}</p>
  );

  const extraDescription = (
    <Fragment>
      <p className="size-16 mt-1 mb-0">
        Our process is rigorous: We spend hundreds of miles behind the wheel, living with each vehicle in real-world
        conditions to understand how well it meets daily needs. Unlike most publications, we also conduct instrumented
        testing at our dedicated test track, providing empirical data to support every review. What you&apos;ll find
        here is our honest, unbiased and fully independent evaluation of each {typeCopySingularLowercase} and how it
        stacks up against the competition. Read more about{' '}
        <a
          href="/car-reviews/features/how-we-test-cars-and-trucks.html"
          rel="noopener noreferrer"
          className="how-we-rank-link"
        >
          {subtypeTitle ? `how we test ${subtypeTitle}s` : `how we test ${typeCopySingularLowercase}s`}
        </a>
        .
      </p>
    </Fragment>
  );

  return (
    <header className="pos-r hero-content-wrapper is-laurel-type" style={{ minHeight: '256px' }}>
      <div className="svg-wrapper">
        <div className="pos-a w-100 hidden-sm-down d-flex justify-content-center" style={DESKTOP_BG_STYLE}>
          <img
            src={HERO_BG_PATHS_LAUREL.lg}
            alt=""
            fetchPriority={'high'}
            className="pos-a"
            aria-hidden
            height="100%"
            width="100%"
            style={{ objectFit: 'cover' }}
          />
          <img
            src={getStaticImageUrl('/type-page/laurelOnlyBgLg.svg')}
            alt=""
            fetchPriority={'high'}
            className="pos-a"
            aria-hidden
            height="100%"
            width="100%"
            style={{ objectFit: 'contain' }}
          />
        </div>
        <div className="pos-a w-100 hidden-sm-up" style={MOBILE_BG_STYLE}>
          <img
            src={HERO_BG_PATHS_LAUREL.sm}
            alt=""
            fetchPriority={'high'}
            className="pos-a"
            aria-hidden
            height="100%"
            width="100%"
            style={{ objectFit: 'cover', objectPosition: 'top' }}
          />
        </div>
      </div>
      <Container>
        <Row data-tracking-parent="edm-entry-summary" className="">
          <Col xs={12}>
            <Breadcrumbs
              className="px-0 m-0 pt-0_5 pb-0 disclaimer text-shadow-contrast"
              olClassName={null}
              itemClassName="text-capitalize"
              data={breadcrumbsData}
            />
          </Col>
          <Col xs={12} className="px-1 px-md-0">
            <div
              className="details-container bg-white rounded-16 py-1 py-md-2 px-1_25 px-md-2 mt-2 mt-md-3_5 mb-1_5 mb-md-2 mx-auto"
              style={{ maxWidth: '945px' }}
            >
              <div className="d-flex">
                {!isChildPage && (
                  <Fragment>
                    <div
                      className="flag-container me-1 me-md-2 pos-r d-flex flex-column align-items-center hidden-md-down"
                      style={{ minWidth: '118px', height: '145px' }}
                      aria-hidden
                    >
                      <img
                        src={BG_FLAG_WITH_LAUREL}
                        alt=""
                        fetchPriority={'low'}
                        className="pos-a"
                        aria-hidden
                        height="100%"
                        width="100%"
                      />
                      <div className="pos-r" style={{ top: '2rem' }}>
                        {getIcon(type)}
                      </div>
                      <span
                        className="pos-r heading-7 text-center text-white d-block text-capitalize"
                        style={{ top: '1.5rem' }}
                      >
                        {typeDisplayCopy}
                      </span>
                    </div>
                    <div
                      className="flag-container me-1 me-md-2 pos-r d-flex flex-column align-items-center hidden-md-up"
                      style={{ minWidth: '64px', height: '79px' }}
                      aria-hidden
                    >
                      <img
                        src={BG_FLAG_WITH_LAUREL}
                        alt=""
                        fetchPriority={'low'}
                        className="pos-a"
                        aria-hidden
                        height="100%"
                        width="100%"
                      />
                      <div className="pos-r" style={{ top: '0.3rem', transform: 'scale(0.55)' }}>
                        {getIcon(type)}
                      </div>
                      <span
                        className="pos-r heading-7 text-center text-white size-12 d-block text-capitalize"
                        style={{ top: '-0.7rem' }}
                      >
                        {typeDisplayCopy}
                      </span>
                    </div>
                  </Fragment>
                )}
                <div className="how-we-rank-wrapper">
                  <h1 id="type-page-heading" className="mb-0_75 text-cool-gray-10 mt-0_25 mt-sm-0_5 mt-md-0">
                    <span className="hidden-md-down size-36">{title}</span>
                    <span className="hidden-md-up size-24">{title}</span>
                  </h1>
                  <div className="hidden-md-down text-cool-gray-30">
                    <ReadMore baseDescriptionCopy={descriptionCopy} summaryClasses="size-16 mt-1">
                      {extraDescription}
                    </ReadMore>
                  </div>
                </div>
              </div>
              <div className="hidden-md-up text-cool-gray-30 mt-1">
                <ReadMore baseDescriptionCopy={descriptionCopy} summaryClasses="size-16 mt-1">
                  {extraDescription}
                </ReadMore>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </header>
  );
}

HeroContentLaurel.propTypes = {
  breadcrumbsData: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
  type: PropTypes.string,
  subtypeTitle: PropTypes.string,
};
